import React, { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { CalendarDays, Send } from "lucide-react";
import { BRAND, IMAGES } from "../lib/site";
import { PageHero } from "../components/Sections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = ["Brazilian Butt Lift", "Breast Enhancement", "Body Contouring", "Recovery Center", "Other"];

export default function ConsultationsPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "Brazilian Butt Lift",
    preferred_date: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please complete the required fields.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/consultations`, form);
      // Open the user's email client with the request pre-filled
      const subject = encodeURIComponent(`Consultation Request — ${form.service}`);
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nService: ${form.service}\nPreferred Date: ${form.preferred_date || "—"}\n\nMessage:\n${form.message || "—"}`
      );
      window.location.href = `mailto:${BRAND.email}?subject=${subject}&body=${body}`;
      toast.success("Consultation request received. Opening your email to confirm…");
      setDone(true);
      setForm({ name: "", email: "", phone: "", service: "Brazilian Butt Lift", preferred_date: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + BRAND.phone);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-consultations">
      <PageHero
        kicker="Private Consultations"
        title="Reserve a"
        accent="confidential audience"
        subtitle="Tell us a little about you. Our surgical concierge will respond personally within one business day to schedule your private consultation."
        image={IMAGES.floral}
        testid="consult-hero"
      />

      <section className="py-20 md:py-28 bg-mag">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <h3 className="font-heading text-3xl text-white">What to expect</h3>
            <p className="text-white/65 mt-5 font-heading text-lg leading-relaxed">
              Your consultation includes a private surgical review, 3D
              imaging, recovery center tour and a candid conversation about
              artistry, safety and investment.
            </p>
            <div className="gold-divider my-8" />
            <div className="space-y-4 text-white/75 font-body text-sm">
              <p><span className="text-gold tracking-[0.25em] uppercase text-xs">Duration · </span>60–90 minutes</p>
              <p><span className="text-gold tracking-[0.25em] uppercase text-xs">Format · </span>In-person, by appointment</p>
              <p><span className="text-gold tracking-[0.25em] uppercase text-xs">Discretion · </span>Private entry &amp; suite</p>
              <p><span className="text-gold tracking-[0.25em] uppercase text-xs">Phone · </span><a href={`tel:+1${BRAND.phoneHref}`} className="text-gold hover:text-pink-luxe">{BRAND.phone}</a></p>
              <p><span className="text-gold tracking-[0.25em] uppercase text-xs">Email · </span><a href={`mailto:${BRAND.email}`} className="text-gold hover:text-pink-luxe">{BRAND.email}</a></p>
            </div>
          </div>

          <div className="md:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="consultation-form"
              className="mag-card p-8 md:p-10 space-y-5"
            >
              <h3 className="font-heading text-3xl text-white">Request Consultation</h3>
              <p className="text-white/55 text-sm">All information is held in HIPAA-compliant confidence.</p>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-[11px] tracking-[0.3em] uppercase text-gold">Full Name *</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    data-testid="consult-input-name"
                    className="mag-input mt-2"
                    placeholder="Sienna R."
                  />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.3em] uppercase text-gold">Email *</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    data-testid="consult-input-email"
                    className="mag-input mt-2"
                    placeholder="you@email.com"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-[11px] tracking-[0.3em] uppercase text-gold">Phone *</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    required
                    data-testid="consult-input-phone"
                    className="mag-input mt-2"
                    placeholder="(702) 000-0000"
                  />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.3em] uppercase text-gold">Service of Interest</label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={onChange}
                    data-testid="consult-input-service"
                    className="mag-input mt-2 appearance-none cursor-pointer"
                  >
                    {services.map((s) => (
                      <option key={s} value={s} className="bg-mag-2 text-white">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.3em] uppercase text-gold flex items-center gap-2">
                  <CalendarDays size={14} /> Preferred Date
                </label>
                <input
                  type="date"
                  name="preferred_date"
                  value={form.preferred_date}
                  onChange={onChange}
                  data-testid="consult-input-date"
                  className="mag-input mt-2"
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.3em] uppercase text-gold">Tell us a little more</label>
                <textarea
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  data-testid="consult-input-message"
                  className="mag-input mt-2"
                  placeholder="Goals, timeline, any past procedures…"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full justify-center disabled:opacity-60"
                data-testid="consult-submit-btn"
              >
                {submitting ? "Sending..." : (<>Submit Request <Send size={14} /></>)}
              </button>

              {done && (
                <p className="text-pink-luxe text-sm text-center" data-testid="consult-success-msg">
                  Thank you. Your request has been received.
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
